import { useNumberInput, UseNumberInputProps } from '@chakra-ui/react';
import { Input, InputProps, InputVariant } from '@pluxee-design-system/core';
import { SkippableBaseControlProps } from '@pluxee-design-system/react-hook-form';
import { getVariant } from 'common/forms/utils';
import { useCallback, useMemo } from 'react';
import { useController } from 'react-hook-form';

interface MoneyControlProps
  extends Omit<InputProps, 'isDisabled' | 'format' | 'name' | 'value' | 'variant' | 'type'>,
    SkippableBaseControlProps {
  allowNegative?: boolean;
  precision?: number;
  thousandSeparator?: string;
}

const format = (x: number, separator = ' '): string => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
};

const parse = (x: string, separator = ' '): string => {
  return x.replaceAll(separator, '');
};

const MoneyControl = ({
  allowNegative = false,
  control,
  disabled,
  name,
  thousandSeparator: _skipThousandSeparator = ' ',
  precision = 0,
  readonly,
  rules,
  skipSuccess,
  skipVariant = false,
  onChange: _,
  ...inputProps
}: MoneyControlProps) => {
  const { field, fieldState, formState } = useController({
    name,
    control,
    rules,
  });
  const { onBlur, onChange } = field;
  const { error } = fieldState;
  const variant = getVariant(
    formState,
    fieldState,
    inputProps.isReadOnly ?? readonly,
    skipSuccess,
    skipVariant,
  );

  const handleChange = useCallback(
    (_valueAsString: string, valueAsNumber: number) =>
      onChange(isNaN(valueAsNumber) ? '' : valueAsNumber),
    [onChange],
  );
  const props = useMemo<UseNumberInputProps>(
    () => ({
      name: field.name,
      id: inputProps.id,
      value: field.value,
      min: allowNegative ? undefined : Math.min(1, precision),
      isReadOnly: inputProps.isReadOnly || readonly,
      isDisabled: disabled,
      precision,
      onBlur,
      onChange: handleChange,
      format,
      parse,
    }),
    [
      allowNegative,
      disabled,
      field.name,
      field.value,
      onBlur,
      handleChange,
      inputProps.id,
      inputProps.isReadOnly,
      precision,
      readonly,
    ],
  );
  const { getInputProps, isDisabled, isReadOnly, value: valueAsString } = useNumberInput(props);

  return (
    <Input
      {...inputProps}
      disabled={isReadOnly ? undefined : isDisabled}
      variant={
        variant === InputVariant.SUCCESS && inputProps.rightElement ? InputVariant.FILLED : variant
      }
      value={valueAsString}
      helpText={error?.message ?? inputProps.helpText}
      {...getInputProps()}
      format={undefined}
    />
  );
};

export default MoneyControl;
